import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import {
  FileWarning, Plus, Search, X, Edit2, Trash2, Save, Download, Upload, Clock, CheckSquare, Camera,
} from 'lucide-react'
import { issueApi } from '../services/issueApi'
import { exportToCsv } from '../services/csvExport'
import { projectApi } from '../services/projectApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { StatusBadge } from '../components/common/StatusBadge'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { CsvImportDialog, type ImportField } from '../components/common/CsvImportDialog'
import type {
  Issue, IssueCreate, IssueUpdate, IssuePhoto,
  IssueStatus, IssueType, IssueCategory, IssuePhase,
  PunchItemPriority, Project,
} from '../types'

const TYPE_LABELS: Record<IssueType, string> = {
  Deficiency: 'Deficiency',
  RFI: 'RFI',
  Observation: 'Observation',
  NonConformance: 'Non-Conformance',
  ChangeOrder: 'Change Order',
}

const PHASE_LABELS: Record<IssuePhase, string> = {
  Design: 'Design Review',
  PFAT: 'Pre-Functional',
  FPT: 'Functional Testing',
  Training: 'Training',
  Closeout: 'Closeout',
  Warranty: 'Warranty',
}

export function IssuesLog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [items, setItems] = useState<Issue[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filterProject, setFilterProject] = useState(searchParams.get('project') || '')
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '')
  const [filterType, setFilterType] = useState(searchParams.get('type') || '')
  const [filterPhase, setFilterPhase] = useState(searchParams.get('phase') || '')
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')

  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<Issue | null>(null)
  const [formData, setFormData] = useState({
    projectId: '' as string | number,
    title: '',
    description: '',
    category: 'Other' as IssueCategory,
    type: 'Deficiency' as IssueType,
    priority: 'Medium' as PunchItemPriority,
    status: 'Open' as IssueStatus,
    phase: '' as IssuePhase | '',
    responsibleParty: '',
    assignedTo: '',
    dueDate: '',
    resolution: '',
  })
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [photoFiles, setPhotoFiles] = useState<File[]>([])
  const [photos, setPhotos] = useState<IssuePhoto[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [lightboxPhoto, setLightboxPhoto] = useState<IssuePhoto | null>(null)

  const importFields: ImportField[] = [
    { key: 'title', label: 'Title', required: true },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category', options: [
      { value: 'Design', label: 'Design' }, { value: 'Installation', label: 'Installation' },
      { value: 'Controls', label: 'Controls' }, { value: 'TAB', label: 'TAB' },
      { value: 'Commissioning', label: 'Commissioning' }, { value: 'Safety', label: 'Safety' },
      { value: 'Documentation', label: 'Documentation' }, { value: 'Other', label: 'Other' },
    ]},
    { key: 'type', label: 'Type', options: [
      { value: 'Deficiency', label: 'Deficiency' }, { value: 'RFI', label: 'RFI' },
      { value: 'Observation', label: 'Observation' }, { value: 'NonConformance', label: 'Non-Conformance' },
      { value: 'ChangeOrder', label: 'Change Order' },
    ]},
    { key: 'priority', label: 'Priority', options: [
      { value: 'Critical', label: 'Critical' }, { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' }, { value: 'Low', label: 'Low' },
    ]},
    { key: 'status', label: 'Status', options: [
      { value: 'Open', label: 'Open' }, { value: 'InProgress', label: 'In Progress' },
      { value: 'Closed', label: 'Closed' }, { value: 'Resolved', label: 'Resolved' },
    ]},
    { key: 'phase', label: 'Phase' },
    { key: 'responsibleParty', label: 'Responsible Party' },
    { key: 'location', label: 'Location' },
    { key: 'resolution', label: 'Resolution' },
    { key: 'dueDate', label: 'Due Date' },
  ]

  async function handleImport(rows: Record<string, string>[]): Promise<{ imported: number; errors: string[] }> {
    // Need a projectId — use the current filter or prompt
    const projectId = filterProject || projects[0]?.id
    if (!projectId) return { imported: 0, errors: ['No project selected. Set a project filter first.'] }

    let imported = 0
    const errors: string[] = []

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!
      if (!row.title) {
        errors.push(`Row ${i + 1}: Missing title, skipped`)
        continue
      }
      try {
        await issueApi.create({
          projectId: Number(projectId),
          title: row.title,
          description: row.description || undefined,
          category: (row.category as IssueCategory) || undefined,
          type: (row.type as IssueType) || undefined,
          priority: (row.priority as PunchItemPriority) || undefined,
          phase: (row.phase as IssuePhase) || undefined,
          responsibleParty: row.responsibleParty || undefined,
          dueDate: row.dueDate || undefined,
        })
        imported++
      } catch (err) {
        errors.push(`Row ${i + 1} ("${row.title.substring(0, 30)}"): ${err instanceof Error ? err.message : 'Failed'}`)
      }
    }

    if (imported > 0) loadItems()
    return { imported, errors }
  }

  // Sync filters to URL params
  useEffect(() => {
    const params = new URLSearchParams()
    if (filterProject) params.set('project', filterProject)
    if (filterStatus) params.set('status', filterStatus)
    if (filterType) params.set('type', filterType)
    if (filterPhase) params.set('phase', filterPhase)
    if (searchTerm) params.set('search', searchTerm)
    setSearchParams(params, { replace: true })
  }, [filterProject, filterStatus, filterType, filterPhase, searchTerm, setSearchParams])

  const loadItems = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await issueApi.getAll({
        projectId: filterProject || undefined,
        status: filterStatus || undefined,
        type: filterType || undefined,
        phase: filterPhase || undefined,
      })
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issues')
    } finally {
      setLoading(false)
    }
  }, [filterProject, filterStatus, filterType, filterPhase])

  useEffect(() => { loadItems() }, [loadItems])

  useEffect(() => {
    projectApi.getAll({}).then((result) => setProjects(result.items)).catch(() => {})
  }, [])

  function openCreate() {
    setEditingItem(null)
    setFormData({
      projectId: filterProject || '',
      title: '',
      description: '',
      category: 'Other',
      type: 'Deficiency',
      priority: 'Medium',
      status: 'Open',
      phase: '',
      responsibleParty: '',
      assignedTo: '',
      dueDate: '',
      resolution: '',
    })
    setPhotoFiles([])
    setPhotos([])
    setShowModal(true)
  }

  function openEdit(item: Issue) {
    setEditingItem(item)
    setFormData({
      projectId: item.projectId,
      title: item.title,
      description: item.description || '',
      category: item.category,
      type: item.type,
      priority: item.priority,
      status: item.status,
      phase: item.phase || '',
      responsibleParty: item.responsibleParty || '',
      assignedTo: item.assignedTo || '',
      dueDate: item.dueDate || '',
      resolution: item.resolution || '',
    })
    setPhotoFiles([])
    setPhotos([])
    setShowModal(true)

    // Load photos
    setPhotosLoading(true)
    issueApi.getPhotos(String(item.id)).then(setPhotos).catch(() => {}).finally(() => setPhotosLoading(false))
  }

  async function handleDeletePhoto(photoId: number) {
    if (!editingItem) return
    try {
      await issueApi.deletePhoto(String(editingItem.id), String(photoId))
      setPhotos(prev => prev.filter(p => p.id !== photoId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo')
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      setSaving(true)
      let savedItem: Issue
      if (editingItem) {
        savedItem = await issueApi.update(String(editingItem.id), {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          type: formData.type,
          priority: formData.priority,
          status: formData.status,
          phase: formData.phase || null,
          responsibleParty: formData.responsibleParty || null,
          assignedTo: formData.assignedTo || null,
          dueDate: formData.dueDate || null,
          resolution: formData.resolution || null,
        } as IssueUpdate)
      } else {
        savedItem = await issueApi.create({
          projectId: Number(formData.projectId),
          title: formData.title,
          description: formData.description,
          category: formData.category,
          type: formData.type,
          priority: formData.priority,
          phase: formData.phase || undefined,
          responsibleParty: formData.responsibleParty || undefined,
          assignedTo: formData.assignedTo || undefined,
          dueDate: formData.dueDate || undefined,
        } as IssueCreate)
      }

      // Upload any staged photos
      for (const file of photoFiles) {
        await issueApi.uploadPhoto(String(savedItem.id), file)
      }

      setShowModal(false)
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save issue')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteId) return
    try {
      await issueApi.delete(deleteId)
      setDeleteId(null)
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  async function handleBulkUpdate(updates: { status?: string; priority?: string }) {
    if (selectedIds.size === 0) return
    try {
      setBulkSaving(true)
      await issueApi.bulkUpdate(Array.from(selectedIds), updates)
      setSelectedIds(new Set())
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk update')
    } finally {
      setBulkSaving(false)
    }
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredItems.map(i => i.id)))
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const filteredItems = searchTerm
    ? items.filter((i) =>
        i.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.issueNumber.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items

  function projectName(projectId: number): string {
    const p = projects.find(proj => String(proj.id) === String(projectId))
    return p ? (p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name || `#${projectId}` : `#${projectId}`
  }

  const statusCounts = items.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Issues & Resolutions Log</h1>
          <p className="page-subtitle">Track deficiencies, RFIs, and observations across all Cx phases</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={16} /> Import CSV
          </button>
          {filteredItems.length > 0 && (
            <button className="btn btn-secondary" onClick={() => {
              exportToCsv(`issues_log_${new Date().toISOString().split('T')[0]}.csv`,
                ['Issue #', 'Title', 'Project', 'Type', 'Category', 'Priority', 'Status', 'Phase', 'Responsible Party', 'Assigned To', 'Due Date', 'Resolution', 'Description'],
                filteredItems.map(i => [i.issueNumber, i.title, projectName(i.projectId), i.type, i.category, i.priority, i.status, i.phase || '', i.responsibleParty || '', i.assignedToName || i.assignedTo || '', i.dueDate || '', i.resolution || '', i.description || ''])
              )
            }}>
              <Download size={16} /> Export CSV
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} /> New Issue
          </button>
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {/* Status summary */}
      {items.length > 0 && (
        <div className="d-flex gap-3 flex-wrap mb-4">
          {(['Open', 'InProgress', 'PendingResponse', 'Resolved', 'Closed', 'Deferred'] as IssueStatus[]).map(s => {
            const c = statusCounts[s] || 0
            if (c === 0 && s !== 'Open') return null
            return (
              <button
                key={s}
                className={`btn btn-xs ${filterStatus === s ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterStatus(filterStatus === s ? '' : s)}
              >
                {s === 'InProgress' ? 'In Progress' : s === 'PendingResponse' ? 'Pending Response' : s} ({c})
              </button>
            )
          })}
          {filterStatus && (
            <button className="btn btn-xs btn-ghost" onClick={() => setFilterStatus('')}>Clear</button>
          )}
        </div>
      )}

      <div className="filters-bar">
        <div className="input-group" style={{ flex: '1', maxWidth: '300px' }}>
          <Search size={16} className="input-icon" />
          <input className="form-input search-input" placeholder="Search issues..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select className="form-select" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{(p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name}</option>)}
        </select>
        <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)}>
          <option value="">All Phases</option>
          {Object.entries(PHASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {selectedIds.size > 0 && (
        <div className="info-card mb-4" style={{
          background: 'var(--accent-blue-soft)', border: '1px solid rgba(59, 130, 246, 0.3)',
          display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap',
        }}>
          <div className="d-flex align-center gap-2">
            <CheckSquare size={16} color="var(--accent-blue)" />
            <strong style={{ color: 'var(--accent-blue)' }}>{selectedIds.size} selected</strong>
          </div>
          <div className="d-flex align-center gap-2">
            <span className="text-sm text-secondary-color">Set status:</span>
            {(['Open', 'InProgress', 'PendingResponse', 'Resolved', 'Closed', 'Deferred'] as IssueStatus[]).map(s => (
              <button key={s} className="btn btn-xs btn-ghost" disabled={bulkSaving}
                onClick={() => handleBulkUpdate({ status: s })}>
                {s === 'InProgress' ? 'In Progress' : s === 'PendingResponse' ? 'Pending' : s}
              </button>
            ))}
          </div>
          <div className="d-flex align-center gap-2">
            <span className="text-sm text-secondary-color">Set priority:</span>
            {(['Critical', 'High', 'Medium', 'Low'] as PunchItemPriority[]).map(p => (
              <button key={p} className="btn btn-xs btn-ghost" disabled={bulkSaving}
                onClick={() => handleBulkUpdate({ priority: p })}>
                {p}
              </button>
            ))}
          </div>
          <button className="btn btn-xs btn-ghost" onClick={() => setSelectedIds(new Set())} style={{ marginLeft: 'auto' }}>
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <LoadingSpinner message="Loading issues..." />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          icon={<FileWarning size={48} />}
          title="No issues found"
          message={searchTerm || filterProject || filterStatus || filterType ? 'Try adjusting your filters.' : 'No issues recorded yet. Issues track deficiencies, RFIs, and observations found during commissioning.'}
          action={!searchTerm && !filterProject ? <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Log Issue</button> : undefined}
        />
      ) : (
        <div className="table-container">
          <div className="table-summary">{filteredItems.length} issue{filteredItems.length !== 1 ? 's' : ''}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={selectedIds.size === filteredItems.length && filteredItems.length > 0} onChange={toggleSelectAll} />
                </th>
                <th style={{ width: 90 }}>#</th>
                <th>Title</th>
                <th>Project</th>
                <th>Type</th>
                <th>Phase</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Responsible</th>
                <th>Due</th>
                <th style={{ width: 70 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id} style={selectedIds.has(item.id) ? { background: 'var(--accent-blue-soft)' } : undefined}>
                  <td><input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} /></td>
                  <td className="text-muted" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '12px' }}>{item.issueNumber}</td>
                  <td className="font-medium" style={{ cursor: 'pointer' }} onClick={() => openEdit(item)}>{item.title}</td>
                  <td className="text-secondary-color">{projectName(item.projectId)}</td>
                  <td><span className="badge badge-gray-light">{TYPE_LABELS[item.type] || item.type}</span></td>
                  <td className="text-secondary-color">{item.phase ? PHASE_LABELS[item.phase] || item.phase : '-'}</td>
                  <td><PriorityBadge priority={item.priority} /></td>
                  <td><StatusBadge status={item.status} /></td>
                  <td className="text-secondary-color">{item.responsibleParty || '-'}</td>
                  <td>{item.dueDate ? (
                    (() => {
                      const isOverdue = (item.status === 'Open' || item.status === 'InProgress' || item.status === 'PendingResponse') && item.dueDate! < new Date().toISOString().split('T')[0]!
                      return <span style={isOverdue ? { color: 'var(--accent-red)', fontWeight: 600 } : { color: 'var(--text-secondary)' }}>
                        {isOverdue && <Clock size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />}
                        {new Date(item.dueDate).toLocaleDateString()}
                      </span>
                    })()
                  ) : <span className="text-secondary-color">-</span>}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-xs btn-icon" onClick={() => openEdit(item)}><Edit2 size={14} /></button>
                      <button className="btn btn-ghost btn-xs btn-icon text-danger" onClick={() => setDeleteId(String(item.id))}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg modal-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? `Edit ${editingItem.issueNumber}` : 'New Issue'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editingItem && (
                  <div className="form-group">
                    <label className="required">Project</label>
                    <select className="form-select" required value={formData.projectId} onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}>
                      <option value="">Select project...</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{(p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="required">Title</label>
                  <input className="form-input" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Brief description of the issue" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-select" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value as IssueType })}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-select" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value as IssueCategory })}>
                      <option value="Design">Design</option>
                      <option value="Installation">Installation</option>
                      <option value="Controls">Controls</option>
                      <option value="TAB">TAB</option>
                      <option value="Commissioning">Commissioning</option>
                      <option value="Safety">Safety</option>
                      <option value="Documentation">Documentation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select className="form-select" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value as PunchItemPriority })}>
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cx Phase</label>
                    <select className="form-select" value={formData.phase} onChange={(e) => setFormData({ ...formData, phase: e.target.value as IssuePhase | '' })}>
                      <option value="">Not specified</option>
                      {Object.entries(PHASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Responsible Party</label>
                    <input className="form-input" value={formData.responsibleParty} onChange={(e) => setFormData({ ...formData, responsibleParty: e.target.value })} placeholder="e.g., GC, MEP Sub, Controls" />
                  </div>
                </div>
                {editingItem && (
                  <div className="form-group">
                    <label>Status</label>
                    <select className="form-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as IssueStatus })}>
                      <option value="Open">Open</option>
                      <option value="InProgress">In Progress</option>
                      <option value="PendingResponse">Pending Response</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                      <option value="Deferred">Deferred</option>
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Assigned To</label>
                    <input className="form-input" value={formData.assignedTo} onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })} placeholder="Person responsible for resolution" />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" className="form-input" value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="required">Description</label>
                  <textarea className="form-textarea" required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Detailed description of the issue, what was observed, reference to specs/drawings..." />
                </div>
                {editingItem && (
                  <div className="form-group">
                    <label>Resolution</label>
                    <textarea className="form-textarea" value={formData.resolution} onChange={(e) => setFormData({ ...formData, resolution: e.target.value })} rows={2} placeholder="How was the issue resolved?" />
                  </div>
                )}
                <div className="form-group">
                  <label>Photos</label>
                  <div className="d-flex align-center gap-3 mb-3">
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      <Camera size={14} /> Add Photo{photoFiles.length > 0 ? 's' : ''}
                      <input type="file" accept="image/*" multiple hidden onChange={(e) => {
                        if (e.target.files) setPhotoFiles(prev => [...prev, ...Array.from(e.target.files!)])
                        e.target.value = ''
                      }} />
                    </label>
                    {photoFiles.length > 0 && <span className="text-sm text-secondary-color">{photoFiles.length} file{photoFiles.length !== 1 ? 's' : ''} staged</span>}
                  </div>
                  {photoFiles.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px', marginBottom: photos.length > 0 ? '12px' : 0 }}>
                      {photoFiles.map((file, idx) => (
                        <div key={idx} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--accent-green)', aspectRatio: '1' }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px' }}>
                            <span style={{ background: 'rgba(16,185,129,0.85)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>New</span>
                            <button
                              type="button"
                              onClick={() => setPhotoFiles(prev => prev.filter((_, i) => i !== idx))}
                              style={{ background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              <X size={10} />
                            </button>
                          </div>
                          <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px',
                            padding: '2px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{file.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {photosLoading && <p className="text-sm text-muted">Loading photos...</p>}
                  {photos.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                      {photos.map((photo) => (
                        <div key={photo.id} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border)', aspectRatio: '1' }}>
                          <img
                            src={issueApi.getPhotoUrl(String(editingItem!.id), photo.id)}
                            alt={photo.fileName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => setLightboxPhoto(photo)}
                          />
                          <div style={{
                            position: 'absolute', top: '4px', right: '4px',
                            display: 'flex', gap: '2px',
                          }}>
                            <span style={{
                              background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px',
                              padding: '2px 6px', borderRadius: '4px',
                            }}>{photo.photoType}</span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id) }}
                              style={{
                                background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none',
                                borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
                              }}
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                          {photo.caption && (
                            <div style={{
                              position: 'absolute', bottom: 0, left: 0, right: 0,
                              background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px',
                              padding: '2px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{photo.caption}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <Save size={16} /> {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        title="Delete Issue"
        message="Are you sure you want to delete this issue? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {lightboxPhoto && editingItem && createPortal(
        <div className="modal-overlay" onClick={() => setLightboxPhoto(null)} style={{ zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '90vw', maxHeight: '90vh', position: 'relative',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <img
              src={issueApi.getPhotoUrl(String(editingItem.id), lightboxPhoto.id)}
              alt={lightboxPhoto.fileName}
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 'var(--radius-lg)', objectFit: 'contain' }}
            />
            <div style={{
              marginTop: '12px', color: 'white', textAlign: 'center',
              fontSize: 'var(--font-size-sm)', textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            }}>
              <div className="font-medium">{lightboxPhoto.fileName}</div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {lightboxPhoto.photoType} {lightboxPhoto.caption ? `— ${lightboxPhoto.caption}` : ''}
                {' • '}{new Date(lightboxPhoto.createdAt).toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => setLightboxPhoto(null)}
              style={{
                position: 'absolute', top: '-10px', right: '-10px',
                background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none',
                borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>, document.body
      )}

      <CsvImportDialog
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        title="Import Issues from CSV"
        fields={importFields}
        onImport={handleImport}
      />
    </div>
  )
}
